import React from "react";
import axios from "axios";
import ReactPixel from "react-facebook-pixel";

export interface CapiUserData {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  country?: string;
  client_ip_address?: string;
  client_user_agent?: string;
  fbp?: string;
  fbc?: string;
}

export interface CapiCustomData {
  currency?: string;
  value?: number;
  order_id?: string;
  content_name?: string;
  content_type?: string;
  [key: string]: unknown;
}

export interface TrackEventParams {
  eventName: string;
  eventId: string;
  userData?: CapiUserData;
  customData?: CapiCustomData;
  companyId?: string;
  testEventCode?: string;
}

export function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : undefined;
}

export function cleanUserData(
  rawEmail?: string,
  rawPhone?: string,
  fullName?: string
): CapiUserData {
  const em = rawEmail ? rawEmail.split("@@")[0].trim().toLowerCase() : undefined;
  const ph = rawPhone ? rawPhone.split("@@")[0].replace(/\D/g, "") : undefined;

  let fn: string | undefined;
  let ln: string | undefined;
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    fn = parts[0];
    if (parts.length > 1) {
      ln = parts.slice(1).join(" ");
    }
  }

  return { em, ph, fn, ln };
}

export async function trackCapiAndPixel({
  eventName,
  eventId,
  userData,
  customData,
  companyId,
  testEventCode,
}: TrackEventParams): Promise<void> {
  const resolvedCompanyId = companyId || process.env.REACT_APP_COMPANY_ID;
  const capiUrl =
    process.env.REACT_APP_CAPI_URL || "https://capi-api.universa.vip";

  // 1. Browser Pixel with eventID for deduplication
  if (typeof window !== "undefined") {
    try {
      const win = window as unknown as {
        fbq?: (
          action: string,
          title: string,
          data?: unknown,
          options?: { eventID: string }
        ) => void;
      };
      if (typeof win.fbq === "function") {
        win.fbq("track", eventName, customData, { eventID: eventId });
      } else {
        ReactPixel.track(eventName, customData);
      }
    } catch {
      // ponytail: ignore browser pixel failure
    }
  }

  // 2. Server CAPI via microservice relay
  if (!resolvedCompanyId) return;

  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");

  const payload: {
    test_event_code?: string;
    data: Array<{
      event_name: string;
      event_time: number;
      event_id: string;
      event_source_url?: string;
      action_source: string;
      user_data: CapiUserData;
      custom_data?: CapiCustomData;
    }>;
  } = {
    data: [
      {
        event_name: eventName,
        event_time: Math.floor(Date.now() / 1000),
        event_id: eventId,
        event_source_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        action_source: "website",
        user_data: {
          ...userData,
          ...(fbp ? { fbp } : {}),
          ...(fbc ? { fbc } : {}),
        },
        custom_data: customData,
      },
    ],
  };

  const testCode = testEventCode || process.env.REACT_APP_META_TEST_CODE;
  if (testCode) {
    payload.test_event_code = testCode;
  }

  try {
    await axios.post(
      `${capiUrl}/api/v1/companies/${resolvedCompanyId}/events`,
      payload
    );
  } catch {
    // ponytail: ignore network errors on tracking
  }
}

export interface UsePaymentCapiParams {
  invoiceCode?: string;
  amount?: number;
  description?: string;
  email?: string;
  phone?: string;
  fullName?: string;
  status?: string;
  readyToTrack?: boolean;
  companyId?: string;
}

export function usePaymentCapi({
  invoiceCode,
  amount,
  description,
  email,
  phone,
  fullName,
  status,
  readyToTrack,
  companyId,
}: UsePaymentCapiParams): void {
  const trackedInfoRef = React.useRef(false);
  const trackedPurchaseRef = React.useRef(false);

  // 1. AddPaymentInfo saat instruksi bayar / channel tampil
  React.useEffect(() => {
    if (readyToTrack && invoiceCode && !trackedInfoRef.current) {
      trackedInfoRef.current = true;
      trackCapiAndPixel({
        eventName: "AddPaymentInfo",
        eventId: `payinfo_${invoiceCode}`,
        userData: cleanUserData(email, phone, fullName),
        customData: {
          currency: "IDR",
          value: amount,
          order_id: invoiceCode,
          content_name: description || "Payment",
          content_type: "product",
        },
        companyId,
      });
    }
  }, [readyToTrack, invoiceCode, amount, description, email, phone, fullName, companyId]);

  // 2. Purchase saat settlement / capture
  React.useEffect(() => {
    if (
      invoiceCode &&
      (status === "settlement" || status === "capture") &&
      !trackedPurchaseRef.current
    ) {
      trackedPurchaseRef.current = true;
      trackCapiAndPixel({
        eventName: "Purchase",
        eventId: `purchase_${invoiceCode}`,
        userData: cleanUserData(email, phone, fullName),
        customData: {
          currency: "IDR",
          value: amount,
          order_id: invoiceCode,
          content_name: description || "Payment",
          content_type: "product",
        },
        companyId,
      });
    }
  }, [status, invoiceCode, amount, description, email, phone, fullName, companyId]);
}
